/**
 * URL helper utilities for generating consistent URLs throughout the application.
 * This helps avoid ad-hoc URL construction and ensures consistency.
 */

interface LocationLike {
  pathname: string;
  search?: string;
}

/**
 * Generate a URL to edit a workflow
 * @param workflowId - The workflow ID
 * @returns The URL to edit the workflow
 */
export function getWorkflowEditUrl(workflowId: string): string {
  return `/workflows/edit?id=${workflowId}`;
}

/**
 * Generate a URL to create a new workflow
 * @returns The URL to create a new workflow
 */
export function getWorkflowCreateUrl(): string {
  return "/workflows/edit";
}

/**
 * Generate a URL to view workflow history
 * @returns The URL to view workflow history
 */
export function getWorkflowHistoryUrl(): string {
  return "/workflows/history";
}

/**
 * Generate a URL to view a specific workflow run
 * @param runId - The run ID
 * @returns The URL to view the workflow run
 */
export function getWorkflowRunUrl(runId: string): string {
  return `/workflows/history/${runId}`;
}

/**
 * Generate a URL to view a run, preferring the inline workflow-editor detail
 * when the user is currently inside the editor for a workflow.
 * @param runId - The run ID
 * @param workflowId - The workflow ID associated with the run
 * @param location - Optional location override for testing
 * @returns The contextual URL to view the run
 */
export function getContextualWorkflowRunUrl(
  runId: string,
  workflowId?: string | null,
  location?: LocationLike | null,
): string {
  const currentLocation = location ?? (typeof window !== "undefined" ? window.location : null);

  if (!workflowId || !currentLocation || currentLocation.pathname !== "/workflows/edit") {
    return getWorkflowRunUrl(runId);
  }

  return `/workflows/edit?id=${workflowId}&view=history&runId=${runId}`;
}

/**
 * Generate a URL to edit a workflow draft
 * @param draftId - The draft ID
 * @returns The URL to edit the workflow draft
 */
export function getWorkflowDraftUrl(draftId: string): string {
  return `/workflows/edit?draft=${draftId}`;
}

/**
 * Generate a URL to view the workflow list
 * @returns The URL to view the workflow list
 */
export function getWorkflowListUrl(): string {
  return "/workflows";
}

/**
 * Generate a URL to view an organization's detail page
 * @param organizationId - The organization ID
 * @returns The URL to view the organization
 */
export function getOrganizationUrl(organizationId: string): string {
  return `/organizations/${organizationId}`;
}
